import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { database } from "./firebase";
import { ref, get } from "firebase/database";
import sfRoundedHeavy from "../assets/fonts/SF-Pro-Rounded-Heavy.otf";
import sfRoundedBold from "../assets/fonts/SF-Pro-Rounded-Bold.otf";

const DEFAULT_AVATAR =
  "https://firebasestorage.googleapis.com/v0/b/pokefinder-9c71b.appspot.com/o/logo_img%2Fmoodsmiley.png?alt=media";

/** Fallback when username not resolved yet */
const SHARE_OG_TITLE_FALLBACK = "Add me on MoodStream 💜";
const SHARE_OG_DESCRIPTION =
  "Make photo dumps with friends on MoodStream.";
const SHARE_OG_IMAGE_PATH = "/og-moodstream-profile.png";

function getShareOgImageAbsoluteUrl() {
  if (typeof window === "undefined") return "";
  const base = process.env.PUBLIC_URL || "";
  return `${window.location.origin}${base}${SHARE_OG_IMAGE_PATH}`;
}

const ACCENT = "#f700ff";
const BG_TOP = "#0a0a14";
const BG_BOTTOM = "#32124a";

const PAGE_BG = `linear-gradient(180deg, ${BG_TOP} 0%, ${BG_BOTTOM} 100%)`;

function setMetaProperty(property, content) {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setMetaName(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function ProfileView() {
  const { userId } = useParams();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [profilePic, setProfilePic] = useState(null);
  /** Display name from profile (`profile-data.name`), if set */
  const [profileName, setProfileName] = useState(null);
  const [username, setUsername] = useState(null);
  const [universalLink, setUniversalLink] = useState("");
  const [appOpening, setAppOpening] = useState(false);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById("root");
    const prev = {
      htmlBg: html.style.background,
      htmlBgColor: html.style.backgroundColor,
      htmlMinH: html.style.minHeight,
      htmlAttach: html.style.backgroundAttachment,
      bodyBg: body.style.background,
      bodyBgColor: body.style.backgroundColor,
      bodyMinH: body.style.minHeight,
      bodyAttach: body.style.backgroundAttachment,
      rootMinH: root?.style.minHeight ?? "",
      rootBg: root?.style.background ?? "",
    };
    html.style.background = PAGE_BG;
    html.style.backgroundColor = BG_BOTTOM;
    html.style.minHeight = "100%";
    html.style.backgroundAttachment = "fixed";
    body.style.background = PAGE_BG;
    body.style.backgroundColor = BG_BOTTOM;
    body.style.minHeight = "100%";
    body.style.backgroundAttachment = "fixed";
    if (root) {
      root.style.minHeight = "100%";
      root.style.background = "transparent";
    }
    return () => {
      html.style.background = prev.htmlBg;
      html.style.backgroundColor = prev.htmlBgColor;
      html.style.minHeight = prev.htmlMinH;
      html.style.backgroundAttachment = prev.htmlAttach;
      body.style.background = prev.bodyBg;
      body.style.backgroundColor = prev.bodyBgColor;
      body.style.minHeight = prev.bodyMinH;
      body.style.backgroundAttachment = prev.bodyAttach;
      if (root) {
        root.style.minHeight = prev.rootMinH;
        root.style.background = prev.rootBg;
      }
    };
  }, []);

  const fetchProfile = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    setNotFound(false);

    try {
      const usernameSnap = await get(ref(database, "/usernames/" + userId));
      if (!usernameSnap.exists() || !usernameSnap.val()) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      const keys = Object.keys(usernameSnap.val());
      const uid = keys[0];
      if (!uid) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      const userSnap = await get(ref(database, "/users/" + uid));
      const data = userSnap.val();
      const pd = data?.public?.["profile-data"];

      if (!pd) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      if (pd.downloadURL) setProfilePic(pd.downloadURL);
      setProfileName(
        typeof pd.name === "string" && pd.name.trim() ? pd.name.trim() : null
      );
      setUsername(pd.username || null);

      const name = pd.username || "";
      setUniversalLink(
        `https://app.moodstream.photos/profileView?username=${encodeURIComponent(name)}&uid=${encodeURIComponent(uid)}`
      );
    } catch (err) {
      console.error("ProfileView fetch error:", err);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    if (loading || notFound) return;

    const shareImageUrl =
      profilePic && typeof profilePic === "string"
        ? profilePic
        : getShareOgImageAbsoluteUrl();
    const pageUrl = window.location.href;

    const nameForTitle =
      (profileName && typeof profileName === "string" && profileName.trim()) ||
      (username && typeof username === "string" && username.trim()) ||
      (typeof userId === "string" && userId.trim()) ||
      "";
    const shareOgTitle = nameForTitle
      ? `Add me on MoodStream 💜 - ${nameForTitle}`
      : SHARE_OG_TITLE_FALLBACK;

    document.title = shareOgTitle;

    const metaByProperty = [
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "MoodStream" },
      { property: "og:title", content: shareOgTitle },
      { property: "og:description", content: SHARE_OG_DESCRIPTION },
      { property: "og:image", content: shareImageUrl },
      { property: "og:image:secure_url", content: shareImageUrl },
      {
        property: "og:image:alt",
        content: shareOgTitle,
      },
      { property: "og:url", content: pageUrl },
      { property: "og:locale", content: "en_US" },
    ];

    const metaByName = [
      { name: "description", content: SHARE_OG_DESCRIPTION },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: shareOgTitle },
      { name: "twitter:description", content: SHARE_OG_DESCRIPTION },
      { name: "twitter:image", content: shareImageUrl },
      {
        name: "twitter:image:alt",
        content: shareOgTitle,
      },
    ];

    metaByProperty.forEach(({ property, content }) =>
      setMetaProperty(property, content)
    );
    metaByName.forEach(({ name, content }) => setMetaName(name, content));

    setCanonical(pageUrl);

    const theme = document.querySelector('meta[name="theme-color"]');
    if (!theme) {
      const m = document.createElement("meta");
      m.setAttribute("name", "theme-color");
      m.setAttribute("content", BG_BOTTOM);
      document.head.appendChild(m);
    } else {
      theme.setAttribute("content", BG_BOTTOM);
    }

    return () => {
      document.title = "MoodStream";
    };
  }, [loading, notFound, profilePic, profileName, username, userId]);

  const handleOpenApp = (e) => {
    e?.preventDefault();
    if (!universalLink) return;
    setAppOpening(true);
    window.location.href = universalLink;
    setTimeout(() => setAppOpening(false), 2000);
  };

  if (loading) {
    return (
      <>
        <style>{`
          @keyframes profile-spin { to { transform: rotate(360deg); } }
        `}</style>
        <div
          style={{
            minHeight: "100dvh",
            background: PAGE_BG,
            backgroundColor: BG_BOTTOM,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              width: 44,
              height: 44,
              border: `3px solid rgba(214, 0, 183, 0.25)`,
              borderTop: `3px solid ${ACCENT}`,
              borderRadius: "50%",
              animation: "profile-spin 0.9s linear infinite",
            }}
          />
        </div>
      </>
    );
  }

  if (notFound) {
    return (
      <>
        <style>{`
          .pv-root {
            min-height: 100vh;
            min-height: 100dvh;
            background: ${PAGE_BG};
            background-color: ${BG_BOTTOM};
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 24px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            color: #e8e8ed;
            text-align: center;
          }
        `}</style>
        <div className="pv-root">
          <p style={{ fontSize: 16, opacity: 0.85 }}>
            This profile link isn’t available.
          </p>
        </div>
      </>
    );
  }

  const avatarSrc = profilePic || DEFAULT_AVATAR;

  return (
    <>
      <style>{`
        @font-face {
          font-family: 'SF Pro Rounded Heavy';
          src: url(${sfRoundedHeavy}) format('opentype');
          font-weight: normal;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: 'SF Pro Rounded Bold';
          src: url(${sfRoundedBold}) format('opentype');
          font-weight: normal;
          font-style: normal;
          font-display: swap;
        }
        .pv-root {
          min-height: 100vh;
          min-height: 100dvh;
          background: ${PAGE_BG};
          background-color: ${BG_BOTTOM};
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 32px 24px 48px;
          position: relative;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          box-sizing: border-box;
        }
        .pv-inner {
          width: 100%;
          max-width: 360px;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }
        .pv-avatar-ring {
          width: 168px;
          height: 168px;
          border-radius: 50%;
          padding: 5px;
          background: ${ACCENT};
          margin-bottom: 28px;
          flex-shrink: 0;
        }
        .pv-avatar-ring img {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          object-fit: cover;
          display: block;
          background: ${BG_BOTTOM};
        }
        .pv-title {
          font-family: 'SF Pro Rounded Heavy', -apple-system, BlinkMacSystemFont, sans-serif;
          font-size: 26px;
          font-weight: normal;
          color: #ffffff;
          line-height: 1.25;
          margin: 0 0 12px;
          letter-spacing: -0.02em;
        }
        .pv-tagline {
          font-size: 14;
          font-weight: 600;
          letter-spacing: 0.12em;
          color: rgba(255, 255, 255, 0.45);
          margin: 0 0 36px;
          line-height: 1.4;
        }
        .pv-cta {
          font-family: 'SF Pro Rounded Bold', -apple-system, BlinkMacSystemFont, sans-serif;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          width: 100%;
          padding: 18px 28px;
          border: none;
          border-radius: 999px;
          background: ${ACCENT};
          color: #fff;
          font-size: 17px;
          font-weight: normal;
          cursor: pointer;
          transition: filter 0.2s ease, transform 0.15s ease;
        }
        .pv-cta:hover:not(:disabled) {
          filter: brightness(1.08);
          transform: translateY(-1px);
        }
        .pv-cta:disabled {
          opacity: 0.75;
          cursor: not-allowed;
        }
        .pv-cta-arrow {
          font-size: 1.15em;
          line-height: 1;
        }
        @media (max-width: 380px) {
          .pv-title { font-size: 22px; }
          .pv-avatar-ring { width: 148px; height: 148px; }
        }
      `}</style>

      <div className="pv-root">
        <div className="pv-inner">
          <div className="pv-avatar-ring">
            <img
              src={avatarSrc}
              alt={username ? `@${username}` : "Profile"}
              onError={(e) => {
                e.target.src = DEFAULT_AVATAR;
              }}
            />
          </div>

          <h1 className="pv-title">Add me on MoodStream</h1>
          <p className="pv-tagline">MAKE PHOTO DUMPS WITH FRIENDS</p>

          <button
            type="button"
            className="pv-cta"
            onClick={handleOpenApp}
            disabled={appOpening || !universalLink}
          >
            {appOpening ? "Opening…" : "Open MoodStream"}
            <span className="pv-cta-arrow" aria-hidden>
              →
            </span>
          </button>
        </div>
      </div>
    </>
  );
}
